/**
 * Step 4 — Hit again (Phase 38): one calm card — "the drop hits again
 * when …" on the FO rule card, judged per DROP on its own settled stats,
 * with the action as one settings grid under the outcome (Amount · Repeat ·
 * SMS cap · Sends · Texts · Goes out), the history line
 * and the replay. The mechanics live in one ⓘ.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Repeat } from 'lucide-react';
import type { DropGenTextMix } from '@trafficpulse/shared';

import { HintDot } from '@/components/ui';

import type { DraftFlatRule, DropGenPlanDetail } from '../../../../drop-generator.types';
import { draftTextMix } from '../../../shared/texts/text-mix';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { OnlyWhen } from '../card/only-when/OnlyWhen';
import { VerdictCard } from '../card/VerdictCard';
import { HistoryLine } from '../history/HistoryLine';
import { withSuggestion } from '../history/history-words';
import { ReplayPanel } from '../replay/ReplayPanel';
import { RightNowLine } from '../source/RightNowLine';
import { rowsForSource } from '../source/source-words';
import { HitAgainAction } from './HitAgainAction';
import { HitDuplicateRow } from './HitDuplicateRow';
import { HitWhenRow } from './HitWhenRow';

const HINT =
  'Judged per DROP, not per day: at every check (Evaluate verdicts, or the schedule every 15 minutes) each drop this container sent today is judged — on its own settled stats (SMSWiser’s delivered and bot counts, the tracker’s clicks and conversions for that drop, its DROP tags), or on its list’s own tracker stats over the date range (the formulas’ metrics, LIST tags). A drop hits at most once. When it hits, a new drop is laid out right now: same list, same gateway, the texts of the drop that hit or new texts — ★ proven + ✦ AI (the Texts row below — the same setting as Creative › Text ranking › Config), the amount this action makes — through the list’s capacity and the daily caps like any drop. Auto-send takes it in the same minute when it is on; otherwise it waits as a draft. Duplicate: hit-again drops send with SMSWiser’s Duplicate option — on by default. The hit-again drop is a drop: it is judged too, and hits while the chain allows. When: by default it goes out any time the country window is open — after the container’s last drop too; “Only in this container’s hours” holds a hit that comes after its last drop. Nothing goes out outside the country window, to a stopped group or a burned list — the Live card says it was held, and why.';

export const HitAgainStep: FC<{
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
}> = ({ editor, plan, containerId }) => {
  const { hitAgain, verdict, defaultAmount } = editor.draft;
  const patchRule = (patch: Partial<DraftFlatRule>) => editor.hitAgainPatch({ rule: { ...hitAgain.rule, ...patch } });
  const cfg = draftTextMix(editor.draft);
  // 21/10/2026 — Texts is a Hit again value (Verdicts): it follows the master and a push of Verdicts carries it.
  const onPolicy = (mix: DropGenTextMix | undefined) => editor.hitAgainPatch({ texts: mix ?? null });
  const onDrops = hitAgain.source.kind === 'drops';
  // 20/10/2026 — the sample and the (list rule's) settle time are "Only when" rows inside the conditions.
  const footer = (
    <OnlyWhen
      scope="drop"
      sample={{ value: hitAgain.minSampleSent, onChange: (minSampleSent) => editor.hitAgainPatch({ minSampleSent }), unit: onDrops ? 'sms' : 'visits' }}
      settle={onDrops ? { value: verdict.evaluationDelayMin, onChange: (evaluationDelayMin) => editor.verdictPatch({ evaluationDelayMin }), shared: true } : undefined}
    />
  );
  return (
    <div className={hitAgain.enabled ? 'flex flex-col gap-3' : 'flex flex-col gap-3 opacity-60'}>
      {!hitAgain.enabled ? <p className="rounded-md border border-border bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground">Off — no drop is sent again until this is on.</p> : null}
      <VerdictCard
        card="hitAgain"
        source={hitAgain.source}
        onSource={(source) =>
          editor.hitAgainPatch({
            source,
            rule: {
              ...hitAgain.rule,
              rows: rowsForSource(hitAgain.rule.rows, source.kind),
            },
          })
        }
        icon={Repeat}
        lead={
          <>
            The drop <span className="text-success">hits again</span>
          </>
        }
        aside={<HintDot label={HINT} />}
        rule={hitAgain.rule}
        onPatch={patchRule}
        starter={onDrops ? { metric: 'deliveredRate', op: '>', value: 0.9 } : { metric: 'cr', op: '>', value: 3 }}
        emptyHint="No rule yet — no drop is ever sent again."
        footer={footer}
        outcome={{ tone: 'success', icon: Repeat, label: 'send again now' }}
      >
        <HitAgainAction hitAgain={hitAgain} defaultAmount={defaultAmount} onPatch={editor.hitAgainPatch} cfg={cfg} onPolicy={onPolicy}>
          <HitWhenRow when={hitAgain.when} draft={editor.draft} plan={plan} containerId={containerId} onWhen={(when) => editor.hitAgainPatch({ when })} />
          <HitDuplicateRow duplicate={hitAgain.duplicate} onDuplicate={(duplicate) => editor.hitAgainPatch({ duplicate })} />
        </HitAgainAction>
        {onDrops ? (
          <HistoryLine countryKey={plan.countryKey} id={containerId ?? editor.draft.masterId} kind="container" which="performing" onUse={(s) => patchRule({ rows: withSuggestion(hitAgain.rule.rows, s) })} />
        ) : (
          <RightNowLine countryKey={plan.countryKey} containerId={containerId} card="hitAgain" source={hitAgain.source} rule={hitAgain.rule} minSampleSent={hitAgain.minSampleSent} />
        )}
      </VerdictCard>
      <ReplayPanel kind="hitAgain" draft={editor.draft} plan={plan} containerId={containerId} />
    </div>
  );
};
